import produce from 'immer'

import {
  DeliveryForecast,
  DeliveryForecastItemSplit,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../data/models'
import { requestPatchDeliveryForecastsById, requestPutSplitItem } from '../data'
import { areItemSplitsModified } from './areItemSplitsModified'

/**
 * Rejects item splits and updates the ConfirmationCode.
 *
 * @param {string} deliveryForecastId - The ID of the delivery forecast.
 * @param {string} itemId - The ID of the item.
 * @param {DeliveryForecastItemSplit[]} rejectedItemSplits - An array of rejected DeliveryForecastItemSplit objects.
 * @param {DeliveryForecastItemSplit[]} allItemSplits - An array of all DeliveryForecastItemSplit objects.
 */
export const rejectItemSplits = async (
  deliveryForecastId: string,
  itemId: string,
  rejectedItemSplits: DeliveryForecastItemSplit[],
  allItemSplits: DeliveryForecastItemSplit[],
) => {
  const newRejectedItemSplits = produce(rejectedItemSplits, draft => {
    draft.forEach(item => {
      item.ConfirmationDeliveryQuantity = 0
      item.QuantityDeviation = 0
      item.Status = 'REJECTED'
    })
  })

  const rejectedBuyerItems = getRejectedBuyerItems(newRejectedItemSplits)
  const hasBuyerItems = rejectedBuyerItems.length > 0

  const splitItemsToCheck = allItemSplits.filter(
    item =>
      !rejectedItemSplits.some(rejectedItem => item.Id === rejectedItem.Id),
  )

  const hasModifiedItemSplits = areItemSplitsModified(splitItemsToCheck)
  const newConfirmationCode =
    hasModifiedItemSplits || hasBuyerItems ? 'MODIFIED' : 'NOT_SET'

  await patchConfirmationCode(deliveryForecastId, newConfirmationCode)

  for (const item of newRejectedItemSplits) {
    await requestPutSplitItem(deliveryForecastId, itemId, item.Id!, item)
  }
}

/**
 * Filters out the rejected items that not belong to the buyer.
 *
 * @param {DeliveryForecastItemSplit[]} rejectItemSplits - An array of rejected DeliveryForecastItemSplit objects.
 * @returns {DeliveryForecastItemSplit[]} An array of rejected buyer DeliveryForecastItemSplit objects.
 */
const getRejectedBuyerItems = (
  rejectItemSplits: DeliveryForecastItemSplit[],
) => {
  const rejectedBuyerItems = rejectItemSplits.filter(
    item => item.BuyerDeliveryQuantity > 0,
  )

  return rejectedBuyerItems
}

/**
 * Updates the confirmation code of the delivery forecast.
 *
 * @param {string} deliveryForecastId - The ID of the delivery forecast.
 * @param {DeliveryForecast['ConfirmationCode']} newConfirmationCode - The new confirmation code.
 */
const patchConfirmationCode = async (
  deliveryForecastId: string,
  newConfirmationCode: DeliveryForecast['ConfirmationCode'],
) => {
  const patchData: PatchRequest<WebEdiDeliveryForecastDto<DeliveryForecast>> = [
    {
      op: 'replace',
      path: '/Payload/ConfirmationCode',
      value: newConfirmationCode,
    },
  ]

  await requestPatchDeliveryForecastsById(deliveryForecastId, patchData)
}
